// =============================================================================
// The agent's display name (AVR-827).
//
// An agent *is* its `ai_agent` user — one `voice_agents` row per user — so the
// agent's name is that user's first + last name. That is already what the
// admin table lists, what `call_ingestion_worker` attributes calls to, and
// therefore what Call Explorer labels the agent with; making it the name on
// the card and the header too is what lets one rename show up everywhere.
//
// The prompt config's `title` stays as a fallback for an agent whose user has
// no usable name. It is NOT the primary any more: it lives in the baked config
// JSON, which a reseed replaces wholesale, so a name edited there would not
// survive. `use_case_name` and `slug` are last resorts — never blank, so a
// loaded agent always renders something.
// =============================================================================

// NAME_MAX_LENGTH mirrors `public.users.first_name` / `last_name`
// (String(100)); the rename endpoint rejects anything longer with a 422. It
// bounds each half, not the whole name — see splitAgentName.
export const NAME_MAX_LENGTH = 100

// Length as the server counts it. Python's `len()` counts code points, while
// JS `.length` counts UTF-16 code units — so 51 emoji measure 102 here and 51
// there, and a name the server would accept would be blocked in the form.
export const nameLength = (value) => [...String(value ?? '')].length

// How a typed name lands in the two stored columns: first word to `first_name`,
// everything after it to `last_name`. Mirrors `split_agent_name` in
// src/models/agent_provisioning_schemas.py — the server re-splits what it is
// sent, so this copy exists only to bound the fields before the round trip.
//
// A one-word name has no second half, and `last_name` is NOT NULL with a
// non-empty assertion, so the server stores a single space there. Nothing
// renders it, because buildFullName trims.
export const splitAgentName = (name) => {
  const collapsed = String(name ?? '')
    .trim()
    .replace(/\s+/g, ' ')
  const firstSpace = collapsed.indexOf(' ')
  if (firstSpace === -1) {
    return { first: collapsed, last: '' }
  }
  return { first: collapsed.slice(0, firstSpace), last: collapsed.slice(firstSpace + 1) }
}

// `filter(Boolean)` rather than a template literal: a missing half would
// otherwise leave a stray space, which then reads as a non-empty name.
export const buildFullName = (firstName, lastName) =>
  [firstName, lastName].filter(Boolean).join(' ').trim()

// Resolve the name to show for a roster row from `GET /voice-agents`.
export const resolveAgentName = (agent) =>
  buildFullName(agent?.owner_first_name, agent?.owner_last_name) ||
  agent?.title ||
  agent?.use_case_name ||
  agent?.slug ||
  ''
